import { Button, Icon } from "@aidkitorg/component-library";
import { IconName } from "@aidkitorg/component-library/src/Icon";
import React, { SetStateAction, useState } from "react";
import { MetricComponentConfig } from "./MetricComponentConfig";
import { BarChartComponentConfig } from "./BarChartComponentConfig";
import { PieChartComponentConfig } from "./PieChartComponentConfig";
import { MapComponentConfig } from "./MapComponentConfig";
import { useLocalizedStrings } from "../../../Localization";

type DashboardComponent = {
  key: 'metric' | 'bar_chart' | 'pie_chart' | 'map' | 'report',
  name: string,
  subtitle: string,
  icon: IconName,
  onClick: React.MouseEventHandler<HTMLButtonElement> 
}

const ReportComponentConfig = () => {
  const L = useLocalizedStrings()

  return <div className="min-h-64 items-center justify-center flex">
    <div className="text-gray-700 px-4 text-center">
      <div className="font-medium">
        {L.admin_panel.dashboards.reports_coming_soon}
      </div>
      <div>
        {L.admin_panel.dashboards.reports_msg}
      </div>
    </div>
  </div>
}

type DashboardComponentOptions = {
  options: Array<DashboardComponent>
}

const DashboardComponentOptions = ({ options }: DashboardComponentOptions) => {
  return (
    <>
      {options.map((component, idx) => (
        <div key={idx}>
          <Button 
            className="border !w-full !justify-start"
            variant="secondary"
            onClick={component.onClick}
          >
            <div className="flex items-center gap-x-2">
              <div>
                <Icon type="solid" name={component.icon} role="decorative"/>
              </div>
              <div className="text-left">
                <div>
                  {component.name}
                </div>
                <div className="text-gray-700 font-normal">
                  {component.subtitle}
                </div>
              </div>
            </div>
          </Button>
        </div>
      ))}
    </>
  )
}

export type CreateComponentProps = {
  setDisplayMode: (mode: 'create') => void
}

export const CreateDashboardComponentTab = () => {
  const L = useLocalizedStrings()

  const [displayMode, setDisplayMode] = useState<DashboardComponent['key'] | 'create'>('create')

  const DASHBOARD_COMPONENT_OPTIONS: Array<DashboardComponent> = [
    {
      key: 'metric',
      name: L.admin_panel.dashboards.metric,
      subtitle: L.admin_panel.dashboards.metric_subtitle,
      icon: 'CurrencyDollarIcon',
      onClick: () => setDisplayMode('metric')
    },
    {
      key: 'bar_chart',
      name: L.admin_panel.dashboards.bar_chart,
      subtitle: L.admin_panel.dashboards.bar_chart_subtitle,
      icon: 'ChartBarIcon',
      onClick: () => setDisplayMode('bar_chart')
    },
    {
      key: 'pie_chart',
      name: L.admin_panel.dashboards.pie_chart,
      subtitle: L.admin_panel.dashboards.pie_chart_subtitle,
      icon: 'ChartPieIcon',
      onClick: () => setDisplayMode('pie_chart')
    },
    {
      key: 'map',
      name: L.admin_panel.dashboards.map,
      subtitle: L.admin_panel.dashboards.map_subtitle,
      icon: 'MapIcon',
      onClick: () => setDisplayMode('map')
    },
    {
      key: 'report',
      name: L.admin_panel.dashboards.add_report,
      subtitle: L.admin_panel.dashboards.report_subtitle,
      icon: 'TableCellsIcon',
      onClick: () => setDisplayMode('report')
    }
  ]

  return (
    <div className="flex flex-col gap-y-2 p-2">
      {displayMode !== 'create' && (
        <div>
          <Button size="sm" variant="secondary" onClick={() => setDisplayMode('create')}>Back</Button>
        </div>
      )}
      {displayMode === 'create' && <DashboardComponentOptions options={DASHBOARD_COMPONENT_OPTIONS} />}
      {displayMode === 'metric' && <MetricComponentConfig setDisplayMode={() => setDisplayMode('create')} />}
      {displayMode === 'bar_chart' && <BarChartComponentConfig setDisplayMode={() => setDisplayMode('create')} />}
      {displayMode === 'pie_chart' && <PieChartComponentConfig setDisplayMode={() => setDisplayMode('create')} />}
      {displayMode === 'map' && <MapComponentConfig setDisplayMode={() => setDisplayMode('create')}/>}
      {displayMode === 'report' && <ReportComponentConfig />}
    </div>
  )
}